import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import {
  CheckBulkPermissionsRequest,
  CheckBulkPermissionsResponse,
  CheckPermissionRequest,
  CheckPermissionResponse,
  CreateRelationshipResponse,
  DeleteRelationshipResponse,
  ListRelationshipsResponse,
  Relationship,
  ResourceObjectType,
} from "./types";

import { route } from "../../utils";

export * from "./types";

const ENDPOINTS = {
  CREATE: "/api/v2/org/:organization_id/user/:user_id/create-relationship",
  DELETE: "/api/v2/org/:organization_id/user/:user_id/delete-relationship",
  LIST: "/api/v2/org/:organization_id/user/:user_id/relationships/:resource_object_type/:resource_object_id",
  CHECK_PERMISSION:
    "/api/v2/org/:organization_id/user/:user_id/check-permission",
  CHECK_BULK_PERMISSIONS:
    "/api/v2/org/:organization_id/user/:user_id/check-bulk-permissions",
};

export class RelationsAPI {
  constructor(private client: CohesiveAPIClient) {}

  async createRelationship(
    data: Relationship,
    config?: AxiosRequestConfig,
  ): Promise<boolean> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<
      CreateRelationshipResponse,
      any,
      Relationship
    >(
      route(ENDPOINTS.CREATE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data.success;
  }

  async deleteRelationship(
    data: Relationship,
    config?: AxiosRequestConfig,
  ): Promise<boolean> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<
      DeleteRelationshipResponse,
      any,
      Relationship
    >(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data.success;
  }

  async listRelationships(
    resourceObjectType: ResourceObjectType,
    resourceObjectId: string,
    config?: AxiosRequestConfig,
  ): Promise<Relationship[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<ListRelationshipsResponse>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
        resource_object_type: resourceObjectType,
        resource_object_id: resourceObjectId,
      }),
      config,
    );
    return response.data.data.relationships;
  }

  async checkPermission(
    data: CheckPermissionRequest,
    config?: AxiosRequestConfig,
  ): Promise<boolean> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<
      CheckPermissionResponse,
      any,
      CheckPermissionRequest
    >(
      route(ENDPOINTS.CHECK_PERMISSION, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data.has_permission;
  }

  async checkBulkPermissions(
    data: CheckBulkPermissionsRequest,
    config?: AxiosRequestConfig,
  ): Promise<CheckBulkPermissionsResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<
      CheckBulkPermissionsResponse,
      any,
      CheckBulkPermissionsRequest
    >(
      route(ENDPOINTS.CHECK_BULK_PERMISSIONS, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data;
  }
}
